import { Form, Tooltip } from 'antd';
import { DarwinSelect } from '../Select';
import { DarwinTreeSelect } from '../TreeSelect';
import { DarwinCheckbox } from '../Checkbox';
import { DarwinDatePicker } from '../DatePicker';
import type { FilterField } from './types';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styles from './index.module.css';

interface FieldRendererProps {
  field: FilterField;
}

/**
 * Renders a single field based on its type with proper Form.Item wrapper
 */
const RENDERERS: Record<string, (field: any) => React.ReactNode> = {
  select: field => (
    <DarwinSelect
      options={field.options}
      mode={field.mode}
      placeholder={field.placeholder}
      allowClear={field.allowClear}
      showSearch={field.showSearch}
      filterOption={field.filterOption}
      disabled={field.disabled}
    />
  ),
  treeSelect: field => (
    <DarwinTreeSelect
      treeData={field.treeData}
      placeholder={field.placeholder}
      allowClear={field.allowClear}
      showSearch={field.showSearch}
      multiple={field.multiple}
      treeCheckable={field.treeCheckable}
      filterTreeNode={field.filterTreeNode}
    />
  ),
  checkbox: field => (
    <DarwinCheckbox disabled={field.disabled}>
      {field.checkboxLabel || field.label}
    </DarwinCheckbox>
  ),
  datePicker: field => (
    <DarwinDatePicker
      placeholder={field.placeholder}
      disabled={field.disabled}
      allowClear={field.allowClear}
      picker={field.picker}
      style={{ width: '100%' }}
    />
  ),
};

/**
 * Renders a single field based on its type with proper Form.Item wrapper
 */
export const FieldRenderer = ({ field }: FieldRendererProps) => {
  const { name, label, rules, tooltip, type } = field;

  // Build label with optional tooltip
  const fieldLabel = tooltip ? (
    <span className={styles.fieldLabel}>
      {label}{' '}
      <Tooltip title={tooltip}>
        <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
      </Tooltip>
    </span>
  ) : (
    <span className={styles.fieldLabel}>{label}</span>
  );

  const renderField = RENDERERS[type];

  if (!renderField) {
    return null;
  }

  // Special handling for checkbox (valuePropName)
  const valuePropName = type === 'checkbox' ? 'checked' : 'value';

  return (
    <Form.Item
      name={name}
      label={fieldLabel}
      rules={rules}
      valuePropName={valuePropName}
    >
      {renderField(field)}
    </Form.Item>
  );
};
